import { MapElementEnum } from '../constants/map'
import { GeojsonCoordinate } from '../types/map'
import { gcj02towgs84, wgs84togcj02 } from '../vendors/coordtransform.js'

export type MapCoordinateTransform = 'wgs84-gcj02' | 'gcj02-wgs84'

type CoordinateTransform = (longitude: number, latitude: number) => number[]
const COORDINATE_EPSILON = 1e-9
const MAX_INVERSE_ITERATIONS = 8
const EARTH_RADIUS_METERS = 6371008.8

export const COCKPIT_FLIGHT_TRACK_MIN_DISTANCE_METERS = 2
export const COCKPIT_FLIGHT_TRACK_MAX_POINTS = 12000
export const COCKPIT_FLIGHT_TRACK_STROKE_WEIGHT = 3
export const COCKPIT_FLIGHT_TRACK_OUTLINE_WEIGHT = 1
export const COCKPIT_FLIGHT_TRACK_STROKE_STYLE = 'dashed'
export const COCKPIT_FLIGHT_TRACK_STROKE_DASHARRAY = [12, 8]
export const DEFAULT_MAP_CENTER_WGS84: GeojsonCoordinate = [113.943225499, 22.577673716]
export const DEFAULT_MAP_CENTER_GCJ02: GeojsonCoordinate = transformMapCoordinate(
  DEFAULT_MAP_CENTER_WGS84,
  'wgs84-gcj02'
)

function cloneValue<T> (value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

export function isValidWgs84Coordinate (coordinate: GeojsonCoordinate | null | undefined): coordinate is GeojsonCoordinate {
  if (!coordinate) {
    return false
  }
  const longitude = Number(coordinate[0])
  const latitude = Number(coordinate[1])
  return Number.isFinite(longitude) && Number.isFinite(latitude) &&
    Math.abs(longitude) <= 180 && Math.abs(latitude) <= 90 &&
    !(longitude === 0 && latitude === 0)
}

export function selectCockpitMapCenter (
  airportCoordinate: GeojsonCoordinate | null | undefined,
  aircraftCoordinate: GeojsonCoordinate | null | undefined,
  followAircraft: boolean
): GeojsonCoordinate | null {
  const preferred = followAircraft ? aircraftCoordinate : airportCoordinate
  const fallback = followAircraft ? airportCoordinate : aircraftCoordinate
  const selected = isValidWgs84Coordinate(preferred)
    ? preferred
    : isValidWgs84Coordinate(fallback) ? fallback : null
  return selected ? [selected[0], selected[1]] : null
}

export function geographicDistanceMeters (from: GeojsonCoordinate, to: GeojsonCoordinate): number {
  const toRadians = (value: number) => value * Math.PI / 180
  const latitudeDelta = toRadians(to[1] - from[1])
  const longitudeDelta = toRadians(to[0] - from[0])
  const fromLatitude = toRadians(from[1])
  const toLatitude = toRadians(to[1])
  const haversine = Math.sin(latitudeDelta / 2) ** 2 +
    Math.cos(fromLatitude) * Math.cos(toLatitude) * Math.sin(longitudeDelta / 2) ** 2
  return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(haversine)))
}

export function appendFlightTrackCoordinate (
  track: GeojsonCoordinate[],
  coordinate: GeojsonCoordinate,
  minDistanceMeters = COCKPIT_FLIGHT_TRACK_MIN_DISTANCE_METERS,
  maxPoints = COCKPIT_FLIGHT_TRACK_MAX_POINTS
): boolean {
  if (!isValidWgs84Coordinate(coordinate)) {
    return false
  }
  const previous = track[track.length - 1]
  if (previous && geographicDistanceMeters(previous, coordinate) < Math.max(0, minDistanceMeters)) {
    return false
  }
  track.push([coordinate[0], coordinate[1]])
  if (track.length > Math.max(2, maxPoints)) {
    track.splice(0, track.length - Math.max(2, maxPoints))
  }
  return true
}

export function transformMapCoordinate (
  coordinate: GeojsonCoordinate,
  direction: MapCoordinateTransform
): GeojsonCoordinate {
  const transform: CoordinateTransform = direction === 'wgs84-gcj02'
    ? wgs84togcj02
    : gcj02towgs84Exact
  const transformed = transform(coordinate[0], coordinate[1])
  if (coordinate.length < 3) {
    return [transformed[0], transformed[1]]
  }
  return [transformed[0], transformed[1], coordinate[2]]
}

function gcj02towgs84Exact (longitude: number, latitude: number): number[] {
  let transformed = gcj02towgs84(longitude, latitude)
  for (let index = 0; index < MAX_INVERSE_ITERATIONS; index += 1) {
    const projected = wgs84togcj02(transformed[0], transformed[1])
    const longitudeDelta = projected[0] - longitude
    const latitudeDelta = projected[1] - latitude
    transformed = [
      transformed[0] - longitudeDelta,
      transformed[1] - latitudeDelta
    ]
    if (Math.abs(longitudeDelta) < COORDINATE_EPSILON && Math.abs(latitudeDelta) < COORDINATE_EPSILON) {
      break
    }
  }
  return transformed
}

/**
 * Return a cloned map element whose geometry uses the requested coordinate
 * system. API payloads are WGS84; AMap overlays and the frontend store are
 * GCJ-02. The source object is never mutated.
 */
export function transformMapElementCoordinates<T> (
  element: T,
  direction: MapCoordinateTransform
): T {
  const cloned = cloneValue(element) as any
  const resource = cloned?.resource
  const geometry = resource?.content?.geometry
  if (!resource || !geometry) {
    return cloned as T
  }

  if (resource.type === MapElementEnum.PIN || geometry.type === 'Point') {
    geometry.coordinates = transformMapCoordinate(
      geometry.coordinates as GeojsonCoordinate,
      direction
    )
    return cloned as T
  }

  if (resource.type === MapElementEnum.LINE || geometry.type === 'LineString') {
    geometry.coordinates = (geometry.coordinates as GeojsonCoordinate[])
      .map(coordinate => transformMapCoordinate(coordinate, direction))
    return cloned as T
  }

  if (resource.type === MapElementEnum.POLY || geometry.type === 'Polygon') {
    geometry.coordinates = (geometry.coordinates as GeojsonCoordinate[][])
      .map(ring => ring.map(coordinate => transformMapCoordinate(coordinate, direction)))
  }

  return cloned as T
}
